import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  Copy,
  RefreshCw,
  Trash2,
  Clipboard,
  Download,
  AlertTriangle,
  X,
} from "lucide-react";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  generateBulkBarcodes,
  saveBulkBarcodes,
} from "../api/sku";
import {
  downloadBarcodeRegistryExcel,
  getBomBarcodeRegistry,
  deleteBarcodeRegistryRow,
} from "../api/bomApi";
import UPCGenerated from "./UPCGenerated";
import { buildInvalidSkuMessage, parseSkuInput } from "../utils/skuValidation";
import { useAuth } from "../context/AuthContext";
import BarcodeRegistryUploadModal from "../modals/BarcodeRegistryUploadModal";
import { getCostingSettings, updateSccLevels } from "../api/costingSettingsApi";
import toast, { Toaster } from "react-hot-toast";

const SCC_LEVEL_OPTIONS = [10, 20, 30, 40, 50];
const REGISTRY_API_PAGE_SIZE = 50;
const UNIT_OPTIONS = ["un", "CS-6", "Master", "CS-12", "EA", "CS", "Pallet", "Inner", "Pack", "Kg", "Lb"];
const DEFAULT_UNIT = "un";
const FORMAT_10_UNIT = "CS";
const DEFAULT_UNIT_BY_FORMAT = {
  10: "CS",
  20: "Master",
  30: "CS-12",
  40: "Pallet",
  50: "Inner",
};
const normalizeUnit = (value) => {
  const unit = String(value || "").trim();
  return unit || DEFAULT_UNIT;
};
const getAllowedUnitsForIndicator = (indicator) => {
  return UNIT_OPTIONS;
};
const normalizeIndicatorUnit = (value, indicator) => {
  const indicatorNum = Number(indicator);
  const formatDefault = DEFAULT_UNIT_BY_FORMAT[indicatorNum] || DEFAULT_UNIT;
  const incoming = String(value || "").trim();
  const normalized = normalizeUnit(value);
  if (!incoming) return formatDefault;
  if (indicatorNum === 10 && (normalized === DEFAULT_UNIT || normalized === "CS-6")) {
    return FORMAT_10_UNIT;
  }
  return normalized || formatDefault;
};
const getIndicatorsFromRow = (row = {}) => {
  const keys = Object.keys(row?.sccByIndicator || {})
    .map((key) => Number(key))
    .filter((key) => !Number.isNaN(key));
  if (keys.length) return [...new Set(keys)].sort((a, b) => a - b);
  if (!Number.isNaN(Number(row?.sccIndicator))) {
    return [Number(row.sccIndicator)];
  }
  return [];
};

const uomSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    minWidth: '110px',
    borderRadius: '0.375rem',
    borderColor: '#d1d5db',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 6px'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px'
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};
const getFormatUnitsForRow = (row = {}) => {
  const indicators = getIndicatorsFromRow(row);
  const incoming =
    row?.formatUnits && typeof row.formatUnits === "object"
      ? row.formatUnits
      : row?.unitsByFormat && typeof row.unitsByFormat === "object"
        ? row.unitsByFormat
        : {};

  const next = {};
  indicators.forEach((indicator) => {
    const key = String(indicator);
    next[key] = normalizeIndicatorUnit(incoming[key], indicator);
  });
  return next;
};

export default function GeneratedUPCsView() {
  const { canWrite, user } = useAuth();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const canUploadBarcodeRegistry =
    canWrite("CostingGeneratedUPCs") || canWrite("CostingBOMDB");
  const canEditGeneratedSku =
    isAdmin || canWrite("CostingGeneratedUPCsEditSKU");
  const [showBarcodeRegistryModal, setShowBarcodeRegistryModal] =
    useState(false);

  const [upcs, setUpcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const [registryTotalRecords, setRegistryTotalRecords] = useState(0);
  const [registryApiPage, setRegistryApiPage] = useState(1);
  const [registryPageSize, setRegistryPageSize] = useState(REGISTRY_API_PAGE_SIZE);
  const [registrySearch, setRegistrySearch] = useState("");
  const [registryCategory, setRegistryCategory] = useState("all");
  const [registrySystemFilter, setRegistrySystemFilter] = useState("all");
  const [registryErpagFilter, setRegistryErpagFilter] = useState("all");
  const [registryCategories, setRegistryCategories] = useState([]);
  const [registrySortBy, setRegistrySortBy] = useState("");
  const [registrySortOrder, setRegistrySortOrder] = useState("desc");

  const [skuInput, setSkuInput] = useState("");
  const [includeUpcEan, setIncludeUpcEan] = useState(true);
  const [includeScc14, setIncludeScc14] = useState(true);

  const [dbSccOptions, setDbSccOptions] = useState(SCC_LEVEL_OPTIONS);

  const [selectedSccIndicators, setSelectedSccIndicators] = useState(() => {
    const saved = localStorage.getItem("selectedSccIndicators");
    return saved ? JSON.parse(saved) : [10];
  });
  const [upcDisplayMode, setUpcDisplayMode] = useState("upcA");
  const [sccDisplayMode, setSccDisplayMode] = useState("scc14");

  const [generatedRows, setGeneratedRows] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [savingGenerated, setSavingGenerated] = useState(false);
  const [downloadingRegistry, setDownloadingRegistry] = useState(false);
  const [existingSkuModalData, setExistingSkuModalData] = useState(null);

  const [toolError, setToolError] = useState("");
  const [toolSuccess, setToolSuccess] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const sccIndicatorOptions = useMemo(
    () => {
      return dbSccOptions.map((level) => ({
        value: level,
        label: String(level),
      }));
    },
    [dbSccOptions],
  );
  const selectedSccIndicatorOptions = useMemo(
    () =>
      selectedSccIndicators.map((indicator) => ({
        value: indicator,
        label: String(indicator),
      })),
    [selectedSccIndicators],
  );

  useEffect(() => {
    if (toolError || toolSuccess) {
      const timer = setTimeout(() => {
        setToolError("");
        setToolSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toolError, toolSuccess]);

  const loadUPCs = useCallback(async ({
    apiPage = 1,
    limit = registryPageSize,
    search = "",
    category = "all",
    systemFilter = "all",
    erpagFilter = "all",
    sortBy = registrySortBy,
    sortOrder = registrySortOrder,
    sync = false,
  } = {}) => {
    setLoading(true);
    setTableError("");
    try {
      const response = await getBomBarcodeRegistry({
        page: apiPage,
        limit: limit,
        sync: sync ? "true" : undefined,
        search: String(search || "").trim() || undefined,
        category:
          String(category || "").trim().toLowerCase() === "all"
            ? undefined
            : String(category || "").trim(),
        systemGenerated:
          String(systemFilter || "").trim().toLowerCase() === "all"
            ? undefined
            : String(systemFilter || "").trim().toLowerCase() === "system"
              ? "true"
              : "false",
        erpagStatus:
          String(erpagFilter || "").trim().toLowerCase() === "all"
            ? undefined
            : String(erpagFilter || "").trim().toLowerCase(),
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
      });
      if (!response?.success) {
        setTableError(response?.message || "Failed to load records");
        return;
      }
      setUpcs(Array.isArray(response.data) ? response.data : []);
      setRegistryTotalRecords(Number(response.total) || 0);
      setRegistryApiPage(apiPage);
      setRegistryCategories(
        Array.isArray(response.categories) ? response.categories : [],
      );
    } catch (err) {
      setTableError(err?.message || "Failed to load barcode records");
    } finally {
      setLoading(false);
    }
  }, [registryPageSize, registrySortBy, registrySortOrder]);

  useEffect(() => {
    loadUPCs({
      apiPage: 1,
      limit: registryPageSize,
      search: "",
      category: "all",
      systemFilter: "all",
    });

    const loadSettings = async () => {
      try {
        const response = await getCostingSettings();
        if (response?.data?.sccLevels) {
          setDbSccOptions(response.data.sccLevels);
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    };
    loadSettings();
  }, [loadUPCs, registryPageSize]);

  const handleRegistryQueryChange = useCallback(({
    search,
    category,
    systemFilter,
    erpagFilter,
  }) => {
    const nextSearch = String(search || "").trim();
    const nextCategory = String(category || "all").trim() || "all";
    const nextSystemFilter = String(systemFilter || "all").trim() || "all";
    const nextErpagFilter = String(erpagFilter || "all").trim() || "all";

    setRegistrySearch(nextSearch);
    setRegistryCategory(nextCategory);
    setRegistrySystemFilter(nextSystemFilter);
    setRegistryErpagFilter(nextErpagFilter);
    loadUPCs({
      apiPage: 1,
      limit: registryPageSize,
      search: nextSearch,
      category: nextCategory,
      systemFilter: nextSystemFilter,
      erpagFilter: nextErpagFilter,
      sortBy: registrySortBy,
      sortOrder: registrySortOrder,
    });
  }, [loadUPCs, registryPageSize, registrySortBy, registrySortOrder]);

  const handleRegistryPageChange = useCallback((uiPage) => {
    const page = Math.max(Number(uiPage) || 1, 1);
    if (page !== registryApiPage) {
      loadUPCs({
        apiPage: page,
        limit: registryPageSize,
        search: registrySearch,
        category: registryCategory,
        systemFilter: registrySystemFilter,
        erpagFilter: registryErpagFilter,
        sortBy: registrySortBy,
        sortOrder: registrySortOrder,
      });
    }
  }, [
    loadUPCs,
    registryApiPage,
    registryPageSize,
    registryCategory,
    registrySearch,
    registrySystemFilter,
    registryErpagFilter,
    registrySortBy,
    registrySortOrder,
  ]);

  const handleRegistryPageSizeChange = useCallback((newSize) => {
    const size = Math.max(Number(newSize) || 50, 10);
    setRegistryPageSize(size);
    loadUPCs({
      apiPage: 1,
      limit: size,
      search: registrySearch,
      category: registryCategory,
      systemFilter: registrySystemFilter,
      erpagFilter: registryErpagFilter,
      sortBy: registrySortBy,
      sortOrder: registrySortOrder,
    });
  }, [loadUPCs, registryCategory, registrySearch, registrySystemFilter, registryErpagFilter, registrySortBy, registrySortOrder]);

  const handleRegistrySortChange = useCallback(({ sortField, sortOrder }) => {
    setRegistrySortBy(sortField);
    setRegistrySortOrder(sortOrder);
    setRegistryApiPage(1);
    loadUPCs({
      apiPage: 1,
      limit: registryPageSize,
      search: registrySearch,
      category: registryCategory,
      systemFilter: registrySystemFilter,
      erpagFilter: registryErpagFilter,
      sortBy: sortField,
      sortOrder,
    });
  }, [
    loadUPCs,
    registryPageSize,
    registrySearch,
    registryCategory,
    registrySystemFilter,
    registryErpagFilter,
  ]);

  const skuParseResult = useMemo(
    () => parseSkuInput(skuInput, { splitPattern: /\r?\n/, dedupe: true }),
    [skuInput],
  );
  const parsedSkus = skuParseResult.validSkus;

  const skuSummary = useMemo(() => {
    return {
      lines: skuParseResult.totalEntries,
      unique: parsedSkus.length,
      duplicatesRemoved: skuParseResult.duplicatesRemoved,
      invalid: skuParseResult.invalidSkus.length,
    };
  }, [parsedSkus.length, skuParseResult]);

  const copyText = async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
    } catch {
      setToolError("Copy failed. Please copy manually.");
    }
  };
  const copyColumn = (columnKey) => {
    let columnData = [];

    if (columnKey === "sku") {
      columnData = generatedRows.map((row) => row.sku || "");
    } else if (columnKey === "nameEn") {
      columnData = generatedRows.map((row) => row.nameEn || row.description || "");
    } else if (columnKey === "code") {
      columnData = generatedRows.map((row) => row?.[upcDisplayMode] || "");
    } else if (columnKey.startsWith("scc-")) {
      const indicator = columnKey.replace("scc-", "");
      columnData = generatedRows.map((row) => {
        const key = String(indicator);
        return row?.sccByIndicator?.[key]?.[sccDisplayMode] || "";
      });
    } else if (columnKey === "upc-unit") {
      columnData = generatedRows.map((row) => normalizeUnit(row?.upcUnit));
    } else if (columnKey.startsWith("unit-")) {
      const indicator = columnKey.replace("unit-", "");
      columnData = generatedRows.map((row) => {
        const formatUnits = getFormatUnitsForRow(row);
        return (
          formatUnits[String(indicator)] ||
          normalizeIndicatorUnit(undefined, indicator)
        );
      });
    } else if (columnKey === "unit") {
      columnData = generatedRows.map((row) => {
        const indicators = getIndicatorsFromRow(row);
        const formatUnits = getFormatUnitsForRow(row);
        return indicators
          .map((indicator) => `${indicator}:${formatUnits[String(indicator)]}`)
          .join(" | ");
      });
    } else if (columnKey === "source") {
      columnData = generatedRows.map((row) => row.source || "generated");
    }

    const textToCopy = columnData.join("\n");
    copyText(`col-${columnKey}`, textToCopy);
  };

  const handleGenerate = async () => {
    const { validSkus, invalidSkus } = parseSkuInput(skuInput, {
      splitPattern: /\r?\n/,
      dedupe: true,
    });

    const skus = validSkus;

    if (!skus.length) {
      if (invalidSkus.length > 0) {
        setToolError(buildInvalidSkuMessage(invalidSkus.length));
      } else {
        setToolError("Please paste at least one SKU.");
      }
      return;
    }

    setGenerating(true);
    setToolError(
      invalidSkus.length > 0 ? buildInvalidSkuMessage(invalidSkus.length) : "",
    );
    setToolSuccess("");
    try {
      const response = await generateBulkBarcodes({
        skus,
        includeUpcEan,
        includeScc14,
        sccIndicators: selectedSccIndicators,
      });
      if (!response?.success) {
        setToolError(response?.message || "Generation failed");
        return;
      }
      const nextRows = (Array.isArray(response.data) ? response.data : []).map(
        (row) => {
          const normalizedMap =
            row?.sccByIndicator && typeof row.sccByIndicator === "object"
              ? row.sccByIndicator
              : row?.sccIndicator
                ? {
                  [String(row.sccIndicator)]: {
                    scc14: row.scc14 || "",
                    gs1128: row.gs1128 || "",
                    indicator: Number(row.sccIndicator),
                  },
                }
                : {};

          return {
            ...row,
            sccByIndicator: normalizedMap,
            formatUnits: getFormatUnitsForRow({
              ...row,
              sccByIndicator: normalizedMap,
            }),
            upcUnit: normalizeUnit(row?.upcUnit || row?.unit || row?.UNIT),
          };
        },
      );

      const existingSkus = nextRows.filter((row) =>
        String(row.source || "").toLowerCase().includes("existing") ||
        row.isExisting === true ||
        row.alreadyExists === true
      );

      if (existingSkus.length > 0) {
        setExistingSkuModalData({
          existingSkus,
          nextRows,
        });
      } else {
        setGeneratedRows(nextRows);
        setToolSuccess(
          `Successfully generated barcodes for ${nextRows.length} SKU(s).`,
        );
      }
    } catch (err) {
      setToolError(err?.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGenerated = async () => {
    if (!generatedRows.length) return;

    setSavingGenerated(true);
    setToolError("");
    setToolSuccess("");

    try {
      const rowsForSave = generatedRows
        .map((row) => {
          let upcA = String(row.upcA || row.upc || row.upcFull || row.ean13 || "").trim();

          if (!upcA && row.sccByIndicator) {
            const firstScc = Object.values(row.sccByIndicator)[0];
            if (firstScc?.scc14 && firstScc.scc14.length === 14) {
              const base11 = firstScc.scc14.substring(2, 13);
              const digits = base11.split("").map(Number);
              const oddSum = digits.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0);
              const evenSum = digits.filter((_, i) => i % 2 !== 0).reduce((a, b) => a + b, 0);
              const checkDigit = (10 - ((oddSum * 3 + evenSum) % 10)) % 10;
              upcA = `${base11}${checkDigit}`;

              console.log("[UPC-Derivation Formula Log]", {
                sku: row.sku,
                sourceSCC14: firstScc.scc14,
                step1_extractBase11: `firstScc.scc14.substring(2, 13) => ${base11}`,
                step2_formula: "checkDigit = (10 - ((oddSum * 3 + evenSum) % 10)) % 10",
                step2_calculation: `oddSum: ${oddSum}, evenSum: ${evenSum} => checkDigit: ${checkDigit}`,
                final_derived_UPCA: upcA,
                isValid12Digit: upcA.length === 12,
              });
            }
          }

          return {
            sku: String(row.sku || "").trim(),
            nameEn: String(row.nameEn || row.description || "").trim(),
            description: String(row.description || row.nameEn || "").trim(),
            category: String(row.category || row.productCategory || "Manual Barcode Tool").trim(),
            upcA: upcA || "",
            ean13: String(row.ean13 || "").trim(),
            scc14: String(row.scc14 || "").trim(),
            gs1128: String(row.gs1128 || "").trim(),

            sccIndicator: row.sccIndicator,
            sccIndicators: Array.isArray(row.sccIndicators) ? row.sccIndicators : [],
            sccByIndicator: row.sccByIndicator || {},

            upcUnit: normalizeUnit(row?.upcUnit),
            unit: normalizeUnit(row?.upcUnit),
            unitsByFormat: getFormatUnitsForRow(row),

            source: row.source || "generated",
          };
        })
        .filter((row) => {
          const hasUPC = row.upcA && row.upcA.length >= 11;
          const hasSCC = 
            (row.scc14 && row.scc14.length === 14) || 
            Object.values(row.sccByIndicator || {}).some(scc => scc?.scc14 && scc.scc14.length === 14);

          return row.sku && (hasUPC || hasSCC);
        });

      if (rowsForSave.length === 0) {
        setToolError("No valid rows to save. Please make sure you have either UPC-A or SCC-14 generated.");
        return;
      }

      const response = await saveBulkBarcodes({ rows: rowsForSave });

      if (!response?.success) {
        const errMsg = response?.message || "Save failed";
        setToolError(errMsg);
        toast.error(errMsg);
        return;
      }

      await loadUPCs();

      const successMsg = `Successfully saved ${response?.data?.processed || rowsForSave.length} barcode(s) to registry.`;
      setToolSuccess(successMsg);
      toast.success(successMsg);
    } catch (err) {
      console.error(err);
      const errMsg = err?.message || "Save failed unexpectedly";
      setToolError(errMsg);
      toast.error(errMsg);
    } finally {
      setSavingGenerated(false);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text?.trim()) return;
      setSkuInput((prev) =>
        [prev.trim(), text.trim()].filter(Boolean).join("\n"),
      );
    } catch {
      setToolError("Clipboard access denied. Please paste manually.");
    }
  };

  const handleSccIndicatorsChange = async (selected) => {
    const nextValues = (Array.isArray(selected) ? selected : [])
      .map((option) => Number(option.value))
      .filter((value) => !isNaN(value) && value > 0);

    const finalSelection = nextValues.length > 0
        ? [...new Set(nextValues)].sort((a, b) => a - b)
        : [10];

    setSelectedSccIndicators(finalSelection);
    localStorage.setItem("selectedSccIndicators", JSON.stringify(finalSelection));

    const newCustoms = finalSelection.filter(val => !dbSccOptions.includes(val));
    if (newCustoms.length > 0) {
        const updatedDbOptions = [...new Set([...dbSccOptions, ...newCustoms])].sort((a, b) => a - b);
        setDbSccOptions(updatedDbOptions);
        try {
           await updateSccLevels(updatedDbOptions);
        } catch (error) {
           console.error("Failed to update SCC levels in DB", error);
        }
    }
  };

  const handleFormatUnitChange = (rowIndex, indicator, nextUnit) => {
    setGeneratedRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;
        const key = String(indicator);
        const allowedUnits = getAllowedUnitsForIndicator(indicator);
        const normalized = normalizeIndicatorUnit(nextUnit, indicator);
        const finalUnit = normalizeIndicatorUnit(nextUnit, indicator);
        const formatUnits = getFormatUnitsForRow(row);
        return {
          ...row,
          formatUnits: {
            ...formatUnits,
            [key]: finalUnit,
          },
        };
      }),
    );
  };
  const handleUpcUnitChange = (rowIndex, nextUnit) => {
    setGeneratedRows((prev) =>
      prev.map((row, idx) =>
        idx === rowIndex
          ? { ...row, upcUnit: normalizeUnit(nextUnit) }
          : row,
      ),
    );
  };

  const sccIndicatorColumns = useMemo(() => {
    if (!includeScc14) return [];

    const fromRows = new Set();
    generatedRows.forEach((row) => {
      const keys = Object.keys(row?.sccByIndicator || {});
      keys.forEach((key) => {
        const numeric = Number(key);
        if (!Number.isNaN(numeric)) fromRows.add(numeric);
      });
    });

    if (fromRows.size > 0) {
      return [...fromRows].sort((a, b) => a - b);
    }

    return [...selectedSccIndicators].sort((a, b) => a - b);
  }, [generatedRows, includeScc14, selectedSccIndicators]);

  const allGeneratedTsv = generatedRows
    .map((row) => {
      const formatUnits = getFormatUnitsForRow(row);
      const sccColumns = sccIndicatorColumns.flatMap((indicator) => {
        const key = String(indicator);
        return [
          row?.sccByIndicator?.[key]?.[sccDisplayMode] || "",
          formatUnits[key] || normalizeIndicatorUnit(undefined, indicator),
        ];
      });
      return [
        row.sku || "",
        row.nameEn || row.description || "",
        row?.[upcDisplayMode] || "",
        normalizeUnit(row?.upcUnit),
        ...sccColumns,
      ].join("\t");
    })
    .join("\n");

  const exportExcel = () => {
    if (!generatedRows.length) return;

    const codeLabel = upcDisplayMode === "upcA" ? "UPC-A" : "EAN-13";
    const sccLabel = sccDisplayMode === "scc14" ? "SCC-14" : "GS1-128";

    const headers = [
      "SKU",
      "Name",
      codeLabel,
      `Unit (${codeLabel})`,
      ...sccIndicatorColumns.flatMap(
        (indicator, index) => [
          `${sccLabel} Format ${index + 1} (${indicator})`,
          `Unit (${indicator})`,
        ],
      ),
      "Source",
    ];

    const escapeCsv = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const rows = generatedRows.map((row) => {
      const formatUnits = getFormatUnitsForRow(row);
      const formatValues = sccIndicatorColumns.flatMap((indicator) => {
        const key = String(indicator);
        return [
          row?.sccByIndicator?.[key]?.[sccDisplayMode] || "",
          formatUnits[key] || normalizeIndicatorUnit(undefined, indicator),
        ];
      });

      return [
        row?.sku || "",
        row?.nameEn || row?.description || "",
        row?.[upcDisplayMode] || "",
        normalizeUnit(row?.upcUnit),
        ...formatValues,
        row?.source || "",
      ];
    });

    const csvContent = [headers, ...rows]
      .map((line) => line.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `barcode-center-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadRegistryExcel = async () => {
    if (!canUploadBarcodeRegistry || downloadingRegistry) return;
    setDownloadingRegistry(true);
    try {
      const { blob, filename } = await downloadBarcodeRegistryExcel();
      triggerBlobDownload(blob, filename || "barcode-registry.xlsx");
      setToolSuccess("Barcode registry Excel downloaded successfully.");
    } catch (error) {
      setToolError(
        error?.response?.data?.message ||
        error?.message ||
        "Barcode registry download failed",
      );
    } finally {
      setDownloadingRegistry(false);
    }
  };

  const canGenerate = parsedSkus.length > 0 && !generating;
  const canSave = generatedRows.length > 0 && !savingGenerated &&
    generatedRows.some(row => {
      const hasUPC = !!(row.upcA || row.ean13);
      const hasSCC = !!(row.scc14 || Object.keys(row.sccByIndicator || {}).length > 0);
      return hasUPC || hasSCC;

    }); const canClear = Boolean(skuInput.trim());

  return (
    <div className="mx-auto space-y-8 p-6">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
            Barcode Center
          </h1>
          <p className="text-gray-500 mt-1">
            Generate, validate, and store bulk barcode sets.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleDownloadRegistryExcel}
            disabled={!canUploadBarcodeRegistry || downloadingRegistry}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${canUploadBarcodeRegistry && !downloadingRegistry
                ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                : "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
              }`}
          >
            {downloadingRegistry
              ? "Downloading..."
              : "Download Registry Excel"}
          </button>
          <button
            onClick={() => setShowBarcodeRegistryModal(true)}
            disabled={!canUploadBarcodeRegistry}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${canUploadBarcodeRegistry
                ? "bg-black text-white border-black hover:bg-gray-900"
                : "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
              }`}
          >
            Upload Barcode Registry Excel
          </button>
        </div>
      </div>

      {toolError && (
        <div
          className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl text-red-700 flex items-start gap-3"
          role="alert"
        >
          <span className="font-medium">Error:</span> {toolError}
        </div>
      )}
      {toolSuccess && (
        <div
          className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-xl text-emerald-700 flex items-start gap-3"
          role="status"
        >
          <Check className="mt-0.5" size={20} />
          {toolSuccess}
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-8 pt-8 pb-6 border-b border-gray-100">
          <h2 className="text-2xl font-semibold text-gray-900">
            Bulk UPC/EAN + SCC-14 Generator
          </h2>
          <p className="text-gray-600 mt-2">
            Paste SKUs (one per line) - Generate barcodes - Save to prevent
            duplicates.
          </p>
        </div>

        <div className="p-8 space-y-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SKU List
            </label>
            <div className="relative">
              <textarea
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    e.key === "Enter" &&
                    canGenerate
                  ) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
                placeholder="1010100001&#10;1010100025&#10;1010100037"
                className="w-full h-56 font-mono text-sm bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 focus:outline-none focus:border-black resize-none min-h-[180px] placeholder:text-gray-400"
              />
              <button
                onClick={handlePasteFromClipboard}
                className="absolute top-4 right-4 px-4 py-2 text-xs inline-flex items-center gap-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
              >
                <Clipboard size={16} />
                Paste
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-2xl text-sm inline-flex items-center gap-2 border border-gray-200">
                <span className="font-mono font-medium">
                  {skuSummary.lines}
                </span>{" "}
                lines
              </div>
              <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-2xl text-sm inline-flex items-center gap-2 border border-blue-200">
                <span className="font-mono font-medium">
                  {skuSummary.unique}
                </span>{" "}
                unique SKUs
              </div>
              {skuSummary.duplicatesRemoved > 0 && (
                <div className="px-4 py-2 bg-amber-100 text-amber-700 rounded-2xl text-sm border border-amber-200">
                  {skuSummary.duplicatesRemoved} duplicates removed
                </div>
              )}
              {skuSummary.invalid > 0 && (
                <div className="px-4 py-2 bg-red-100 text-red-700 rounded-2xl text-sm border border-red-200">
                  {skuSummary.invalid} invalid SKU
                  {skuSummary.invalid > 1 ? "s" : ""}
                </div>
              )}
              <div className="px-4 py-2 bg-gray-50 text-gray-600 rounded-2xl text-sm border border-gray-200">
                Tip: Press <span className="font-mono">Ctrl+Enter</span> to
                generate
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-10 gap-y-6">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={includeUpcEan}
                onChange={(e) => setIncludeUpcEan(e.target.checked)}
                className="w-5 h-5 accent-black"
              />
              <span className="text-gray-700 group-hover:text-black transition">
                Generate UPC-A + EAN-13
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={includeScc14}
                onChange={(e) => setIncludeScc14(e.target.checked)}
                className="w-5 h-5 accent-black"
              />
              <span className="text-gray-700 group-hover:text-black transition">
                Generate SCC-14 + GS1-128
              </span>
            </label>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-gray-700">SCC Levels</span>
              <div className="min-w-[220px]">
                <CreatableSelect
                  isMulti
                  options={sccIndicatorOptions}
                  value={selectedSccIndicatorOptions}
                  onChange={handleSccIndicatorsChange}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isDisabled={!includeScc14}
                  placeholder="Select SCC levels"
                  classNamePrefix="react-select"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  formatCreateLabel={(inputValue) => `Add case pack "${inputValue}"`}
                  isValidNewOption={(inputValue) => {
                    const val = Number(inputValue);
                    return !isNaN(val) && val > 0 && Number.isInteger(val);
                  }}
                  styles={{
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#f3f4f6" : "white",
                      color: "#111827",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 12px",
                      ":hover": {
                        backgroundColor: "#f9fafb",
                      },
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  components={{
                    Option: ({ children, ...props }) => {
                      const { isSelected } = props;
                      return (
                        <components.Option {...props}>
                          <div className="flex items-center w-full">
                            <div
                              className={`w-5 h-5 border-2 flex items-center justify-center mr-3 rounded transition-all flex-shrink-0
                    ${isSelected
                                  ? "bg-black border-black"
                                  : "border-gray-300 bg-white hover:border-gray-400"
                                }`}
                            >
                              {isSelected && (
                                <Check
                                  size={14}
                                  className="text-white"
                                  strokeWidth={3.5}
                                />
                              )}
                            </div>
                            <span className="flex-1">{children}</span>
                          </div>
                        </components.Option>
                      );
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="gap-3 flex">
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className="w-full min-w-[220px] h-11 px-4 bg-black hover:bg-gray-900 disabled:bg-gray-400 text-white rounded-2xl font-medium inline-flex items-center justify-center gap-3 whitespace-nowrap transition scale-[0.985]"
                >
                  {generating ? (
                    <>Generating...</>
                  ) : (
                    <>
                      Generate Barcodes{" "}
                      <span className="opacity-70">({parsedSkus.length})</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleSaveGenerated}
                  disabled={!canSave}
                  className="w-full min-w-[220px] h-11 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-2xl font-medium inline-flex items-center justify-center gap-3 whitespace-nowrap transition active:scale-[0.985]"
                >
                  {savingGenerated
                    ? "Saving to Database..."
                    : "Save to Database"}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 xl:justify-end">
                {generatedRows.length > 0 && (
                  <>
                    <button
                      onClick={() => copyText("all", allGeneratedTsv)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 bg-white rounded-xl hover:bg-gray-50 text-sm font-medium transition"
                    >
                      {copiedKey === "all" ? (
                        <Check size={18} className="text-emerald-600" />
                      ) : (
                        <Copy size={18} />
                      )}
                      {copiedKey === "all" ? "Copied TSV" : "Copy All (TSV)"}
                    </button>

                    <button
                      onClick={exportExcel}
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 bg-white rounded-xl hover:bg-gray-50 text-sm font-medium transition"
                    >
                      <Download size={16} />
                      Export Excel
                    </button>
                  </>
                )}

                <button
                  onClick={() => setSkuInput("")}
                  disabled={!canClear}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white hover:bg-red-700 rounded-xl disabled:opacity-40 transition"
                >
                  <Trash2 size={18} />
                  Clear Input
                </button>
              </div>
            </div>
          </div>

          {generatedRows.length > 0 ? (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">
                  Generated Barcodes ({generatedRows.length})
                </h3>
                <p className="text-sm text-gray-500">Ready to save or export</p>
              </div>

              <div className="overflow-x-auto rounded-3xl border border-gray-200 max-h-[460px]">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                    <tr>
                      {/* SKU Column */}
                      <th className="px-6 py-4 text-left font-medium text-gray-600">
                        <div className="flex items-center gap-2">
                          SKU
                          <button
                            onClick={() => copyColumn("sku")}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Copy SKU column"
                          >
                            {copiedKey === "col-sku" ? (
                              <Check size={18} className="text-emerald-600" />
                            ) : (
                              <Copy size={18} className="text-gray-500" />
                            )}
                          </button>
                        </div>
                      </th>

                      {/* Name Column */}
                      <th className="px-6 py-4 text-left font-medium text-gray-600">
                        <div className="flex items-center gap-2">
                          Name
                          <button
                            onClick={() => copyColumn("nameEn")}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Copy Name column"
                          >
                            {copiedKey === "col-nameEn" ? (
                              <Check size={18} className="text-emerald-600" />
                            ) : (
                              <Copy size={18} className="text-gray-500" />
                            )}
                          </button>
                        </div>
                      </th>

                      {/* ERPAG Status Column */}
                      <th className="px-6 py-4 text-left font-medium text-gray-600">
                        <div className="flex items-center gap-2">
                          ERPAG Status
                        </div>
                      </th>

                      {/* Code Column */}
                      {includeUpcEan && (
                        <th className="px-6 py-4 text-left font-medium text-gray-600 min-w-[190px]">
                          <div className="flex items-center gap-2">
                            <select
                              value={upcDisplayMode}
                              onChange={(e) => setUpcDisplayMode(e.target.value)}
                              className="border border-gray-300 bg-white rounded-md px-2 py-1 text-xs"
                            >
                              <option value="upcA">UPC-A</option>
                              <option value="ean13">EAN-13</option>
                            </select>
                            <button
                              onClick={() => copyColumn("code")}
                              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Copy Code column"
                            >
                              {copiedKey === "col-code" ? (
                                <Check size={18} className="text-emerald-600" />
                              ) : (
                                <Copy size={18} className="text-gray-500" />
                              )}
                            </button>
                          </div>
                        </th>
                      )}

                      {includeUpcEan && (
                        <th className="px-6 py-4 text-left font-medium text-gray-600 min-w-[150px]">
                          <div className="flex items-center gap-2">
                            Unit
                            <button
                              onClick={() => copyColumn("upc-unit")}
                              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Copy UPC Unit column"
                            >
                              {copiedKey === "col-upc-unit" ? (
                                <Check size={18} className="text-emerald-600" />
                              ) : (
                                <Copy size={18} className="text-gray-500" />
                              )}
                            </button>
                          </div>
                        </th>
                      )}

                      {/* SCC Columns */}
                      {sccIndicatorColumns.map((indicator) => (
                        <React.Fragment key={`scc-head-${indicator}`}>
                          <th className="px-6 py-4 text-left font-medium text-gray-600 min-w-[210px]">
                            <div className="flex items-center gap-2">
                              <span>{`Format (${indicator})`}</span>
                              <select
                                value={sccDisplayMode}
                                onChange={(e) =>
                                  setSccDisplayMode(e.target.value)
                                }
                                className="border border-gray-300 bg-white rounded-md px-2 py-1 text-xs"
                              >
                                <option value="scc14">SCC-14</option>
                                <option value="gs1128">GS1-128</option>
                              </select>
                              <button
                                onClick={() => copyColumn(`scc-${indicator}`)}
                                className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                title={`Copy ${sccDisplayMode} column`}
                              >
                                {copiedKey === `col-scc-${indicator}` ? (
                                  <Check size={18} className="text-emerald-600" />
                                ) : (
                                  <Copy size={18} className="text-gray-500" />
                                )}
                              </button>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left font-medium text-gray-600 min-w-[140px]">
                            <div className="flex items-center gap-2">
                              {`Unit (${indicator})`}
                              <button
                                onClick={() => copyColumn(`unit-${indicator}`)}
                                className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                title={`Copy Unit (${indicator}) column`}
                              >
                                {copiedKey === `col-unit-${indicator}` ? (
                                  <Check
                                    size={18}
                                    className="text-emerald-600"
                                  />
                                ) : (
                                  <Copy size={18} className="text-gray-500" />
                                )}
                              </button>
                            </div>
                          </th>
                        </React.Fragment>
                      ))}

                      {/* Source Column */}
                      <th className="px-6 py-4 font-medium text-gray-600">
                        <div className="flex items-center gap-2">
                          Source
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {generatedRows.map((row, idx) => (
                      <tr
                        key={`${row.sku}-${idx}`}
                        className="odd:bg-white even:bg-gray-50/60 hover:bg-blue-50 transition-colors"
                      >
                        <td className="px-6 py-4 font-mono text-gray-800">
                          {row.sku}
                        </td>
                        <td className="px-6 py-4 text-gray-700 max-w-sm" title={row.nameEn || row.description}>
                          <div className="truncate hover:text-clip hover:whitespace-normal cursor-pointer transition-all" title={row.nameEn || row.description}>
                            {row.nameEn || row.description || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.existsInErpag ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <Check size={13} className="text-emerald-600" />
                              Exists in ERPAG
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              <X size={13} className="text-amber-600" />
                              Not in ERPAG
                            </span>
                          )}
                        </td>

                        {includeUpcEan && (
                          <td className="px-6 py-4 font-mono">
                            {row?.[upcDisplayMode] || ""}
                          </td>
                        )}

                        {includeUpcEan && (
                          <td className="px-6 py-4">
                            <CreatableSelect
                              value={{ label: normalizeUnit(row?.upcUnit), value: normalizeUnit(row?.upcUnit) }}
                              onChange={(selected) =>
                                handleUpcUnitChange(idx, selected ? selected.value : "")
                              }
                              options={UNIT_OPTIONS.map((unit) => ({ label: unit, value: unit }))}
                              styles={uomSelectStyles}
                              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                              menuPosition="fixed"
                              placeholder="Unit"
                            />
                          </td>
                        )}
                        {sccIndicatorColumns.map((indicator) => {
                          const key = String(indicator);
                          const formatUnits = getFormatUnitsForRow(row);
                          const allowedUnits =
                            getAllowedUnitsForIndicator(indicator);
                          const selectedUnit =
                            formatUnits[key] ||
                            normalizeIndicatorUnit(undefined, indicator);
                          return (
                            <React.Fragment
                              key={`scc-val-${row.sku}-${idx}-${indicator}`}
                            >
                              <td className="px-6 py-4 font-mono">
                                {row?.sccByIndicator?.[key]?.[sccDisplayMode] ||
                                  ""}
                              </td>
                              <td className="px-6 py-4">
                                <CreatableSelect
                                  value={{ label: selectedUnit, value: selectedUnit }}
                                  onChange={(selected) =>
                                    handleFormatUnitChange(
                                      idx,
                                      indicator,
                                      selected ? selected.value : ""
                                    )
                                  }
                                  options={allowedUnits.map((unit) => ({ label: unit, value: unit }))}
                                  styles={uomSelectStyles}
                                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                  menuPosition="fixed"
                                  placeholder="Unit"
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            {row.source || "generated"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            !generating && (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/70 px-6 py-10 text-center">
                <p className="text-gray-700 font-medium">
                  No generated rows yet
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Paste SKUs and click{" "}
                  <span className="font-medium">Generate Barcodes</span> to
                  preview results.
                </p>
              </div>
            )
          )}
        </div>
      </div>

      <UPCGenerated
        upcs={upcs}
        loading={loading}
        error={tableError}
        onRefresh={() =>
          loadUPCs({
            apiPage: registryApiPage,
            limit: registryPageSize,
            search: registrySearch,
            category: registryCategory,
            systemFilter: registrySystemFilter,
            erpagFilter: registryErpagFilter,
            sortBy: registrySortBy,
            sortOrder: registrySortOrder,
            sync: true,
          })
        }
        onQueryChange={handleRegistryQueryChange}
        onPageChange={handleRegistryPageChange}
        onPageSizeChange={handleRegistryPageSizeChange}
        sortField={registrySortBy}
        sortOrder={registrySortOrder}
        onSortChange={handleRegistrySortChange}
        totalRecords={registryTotalRecords}
        apiPage={registryApiPage}
        apiPageSize={registryPageSize}
        categories={registryCategories}
        systemFilter={registrySystemFilter}
        erpagFilter={registryErpagFilter}
        canEditSku={canEditGeneratedSku}
        isAdmin={isAdmin}
        onDeleteRow={async (record) => {
          const upcFull = String(record?.upcFull || record?.upc || "").trim();
          await deleteBarcodeRegistryRow({ upcFull });
        }}
      />
      {showBarcodeRegistryModal && (
        <BarcodeRegistryUploadModal
          isOpen={showBarcodeRegistryModal}
          onClose={() => setShowBarcodeRegistryModal(false)}
          canUpload={canUploadBarcodeRegistry}
          onSuccess={() => {
            setShowBarcodeRegistryModal(false);
            loadUPCs({
              apiPage: 1,
              search: registrySearch,
              category: registryCategory,
              systemFilter: registrySystemFilter,
              erpagFilter: registryErpagFilter,
              sortBy: registrySortBy,
              sortOrder: registrySortOrder,
            });
          }}
        />
      )}

      {existingSkuModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-amber-200 shadow-2xl overflow-hidden">
            <div className="flex items-start justify-between border-b border-amber-100 bg-amber-50/80 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-100 text-amber-700 rounded-xl">
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900">
                    SKU Already Exists in Database
                  </h3>
                  <p className="text-xs text-amber-700 font-medium">
                    {existingSkuModalData.existingSkus.length} SKU(s) already found in master product registry
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setExistingSkuModalData(null)}
                className="rounded-lg p-1.5 text-amber-700 hover:bg-amber-100 transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                The requested SKU(s) already exist in your database. Generating or saving barcodes will associate with the pre-existing record:
              </p>

              <div className="max-h-48 overflow-y-auto space-y-2.5 pr-1">
                {existingSkuModalData.existingSkus.map((item, idx) => (
                  <div
                    key={item.sku || idx}
                    className="flex items-center justify-between p-3.5 rounded-xl border border-amber-200 bg-amber-50/50 text-sm"
                  >
                    <div>
                      <span className="font-mono font-bold text-amber-950 block text-base">
                        {item.sku}
                      </span>
                      <span className="text-xs text-gray-600 block mt-0.5">
                        {item.nameEn || item.description || "Existing Master Product"}
                      </span>
                    </div>
                    <span className="px-3 py-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full shadow-xs">
                      {item.source || "existing_product"}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setExistingSkuModalData(null)}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel / Modify SKU
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGeneratedRows(existingSkuModalData.nextRows);
                    setToolSuccess(
                      `Generated barcodes for ${existingSkuModalData.nextRows.length} SKU(s) (including existing products).`
                    );
                    setExistingSkuModalData(null);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800 transition shadow-sm"
                >
                  Proceed & View Barcodes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
